import { Download, Eye, Minus, Pencil, Plus, Settings, SlidersHorizontal } from 'lucide-react'
import {
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useId,
  useRef,
  useState,
} from 'react'
import { usePDFExport } from '../../hooks/use-pdf-export'
import { DEFAULT_CV_LOCALE, SUPPORTED_LOCALES, useI18n } from '../../i18n'
import { usePlatformConfig } from '../../providers/platform-provider'
import type { CV, UpdateCVRequest } from '../../types/cv'
import { CVEditor } from '../editor/cv-editor'
import { PaginatedPreview } from '../preview/paginated-preview'
import './cv-builder.css'

export interface PDFMargins {
  top: number
  right: number
  bottom: number
  left: number
}

export interface CVBuilderProps {
  cv: CV
  onChange: (data: UpdateCVRequest) => void
  previewScale?: number
  devTheme?: 'hive' | null
  className?: string
}

const DEFAULT_EDITOR_WIDTH_PERCENT = 42
const MIN_EDITOR_WIDTH_PERCENT = 30
const MAX_EDITOR_WIDTH_PERCENT = 65
const RESIZE_STEP_PERCENT = 2
const MIN_MARGIN_MM = 0
const MAX_MARGIN_MM = 50
const MARGIN_STEP_MM = 1

type MobilePane = 'editor' | 'preview'

function clampEditorWidth(widthPercent: number): number {
  return Math.min(MAX_EDITOR_WIDTH_PERCENT, Math.max(MIN_EDITOR_WIDTH_PERCENT, widthPercent))
}

function clampMargin(value: number): number {
  return Math.min(MAX_MARGIN_MM, Math.max(MIN_MARGIN_MM, value))
}

/**
 * Main CV Builder component
 * Split-pane layout with editor on left, preview on right
 */
export function CVBuilder({
  cv,
  onChange,
  previewScale = 0.5,
  devTheme = null,
  className = '',
}: CVBuilderProps) {
  const { downloadPDF, isGenerating, error } = usePDFExport()
  const { locale: uiLocale, setLocale, supportedLocales: i18nSupportedLocales, t } = useI18n()
  const builderRef = useRef<HTMLDivElement>(null)
  const config = usePlatformConfig()
  const branding = config.branding
  const canExportPdf = config.features?.pdfExport ?? false
  const supportedLocales = config.locale?.supportedLocales ??
    i18nSupportedLocales ?? [...SUPPORTED_LOCALES]

  const [editorWidthPercent, setEditorWidthPercent] = useState(DEFAULT_EDITOR_WIDTH_PERCENT)
  const [isResizing, setIsResizing] = useState(false)
  const [mobilePane, setMobilePane] = useState<MobilePane>('editor')
  const [margins, setMargins] = useState<PDFMargins>({ top: 15, right: 20, bottom: 15, left: 20 })
  const [showSettings, setShowSettings] = useState(false)
  const [showMarginGuides, setShowMarginGuides] = useState(false)
  const [showContentGuide, setShowContentGuide] = useState(false)
  const [collapseAllSignal, setCollapseAllSignal] = useState(0)
  const settingsId = useId()
  const settingsTitleId = useId()
  const exportErrorId = useId()
  const resizeHandleValue = Math.round(editorWidthPercent)
  const mobilePaneToggleLabel =
    mobilePane === 'editor' ? t('builder.showPreviewPane') : t('builder.showEditorPane')
  const builderStyle = {
    '--cv-builder-editor-width': `${editorWidthPercent}%`,
    ...(branding?.primaryColor ? { '--cv-builder-accent': branding.primaryColor } : {}),
    ...(branding?.accentColor ? { '--cv-builder-accent-hover': branding.accentColor } : {}),
    ...(branding?.fontFamily ? { '--cv-builder-font-family': branding.fontFamily } : {}),
    // Hive theme: retarget the top bar to the embedding hive's colors.
    ...(branding?.topbarColor
      ? { '--cv-builder-topbar-bg': branding.topbarColor, '--cv-builder-topbar-border': branding.topbarColor }
      : {}),
    ...(branding?.topbarTextColor ? { '--cv-builder-topbar-text': branding.topbarTextColor } : {}),
  } as CSSProperties

  const handleExportPDF = async () => {
    const filename = cv.title ? `${cv.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf` : 'cv.pdf'
    await downloadPDF(cv.id, filename, { margins })
  }

  const updateMargin = useCallback((margin: keyof PDFMargins, value: number) => {
    if (!Number.isFinite(value)) return
    setMargins((currentMargins) => ({ ...currentMargins, [margin]: clampMargin(value) }))
  }, [])

  const updateEditorWidthFromPointer = useCallback((clientX: number) => {
    const builderElement = builderRef.current
    if (!builderElement) return

    const rect = builderElement.getBoundingClientRect()
    const nextWidthPercent = ((clientX - rect.left) / rect.width) * 100
    setEditorWidthPercent(clampEditorWidth(nextWidthPercent))
  }, [])

  const handleResizePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return

    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    setIsResizing(true)
    updateEditorWidthFromPointer(event.clientX)
  }

  const handleResizePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!isResizing) return
    updateEditorWidthFromPointer(event.clientX)
  }

  const handleResizePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    setIsResizing(false)
  }

  const handleResizeKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      setEditorWidthPercent((currentWidth) => clampEditorWidth(currentWidth - RESIZE_STEP_PERCENT))
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      setEditorWidthPercent((currentWidth) => clampEditorWidth(currentWidth + RESIZE_STEP_PERCENT))
    }

    if (event.key === 'Home') {
      event.preventDefault()
      setEditorWidthPercent(MIN_EDITOR_WIDTH_PERCENT)
    }

    if (event.key === 'End') {
      event.preventDefault()
      setEditorWidthPercent(MAX_EDITOR_WIDTH_PERCENT)
    }
  }

  return (
    <div
      ref={builderRef}
      className={`cv-builder ${className}`}
      style={builderStyle}
      data-dev-theme={devTheme === 'hive' ? 'hive' : undefined}
      data-mobile-pane={mobilePane}
    >
      <div className="cv-builder-global-header">
        <div className="cv-builder-global-brand">
          {branding?.logo ? (
            <img
              className="cv-builder-global-logo"
              src={branding.logo}
              alt={branding.logoAlt || branding.title || t('app.name')}
            />
          ) : (
            <VHLogoMark />
          )}
          <div className="cv-builder-global-title-stack">
            <div className="cv-builder-global-title">{branding?.title || t('app.name')}</div>
          </div>
        </div>
        <div className="cv-builder-global-controls">
          <LocaleSwitcher
            locales={supportedLocales}
            current={uiLocale}
            onChange={setLocale}
            getLabel={(locale) => t(`locales.${locale}`)}
            ariaLabel={t('builder.uiLanguage')}
          />
        </div>
      </div>

      <div className="cv-builder-editor">
        <div className="cv-builder-editor-header">
          <h2>{t('builder.editCv')}</h2>
          <div className="cv-builder-editor-header-actions">
            <button
              type="button"
              className="cv-builder-collapse-all-btn"
              onClick={() => setCollapseAllSignal((value) => value + 1)}
            >
              {t('actions.collapseAll')}
            </button>
          </div>
        </div>
        <div className="cv-builder-editor-content">
          <CVEditor cv={cv} onChange={onChange} collapseAllSignal={collapseAllSignal} />
        </div>
      </div>

      <div
        className="cv-builder-resize-handle"
        role="separator"
        aria-label={t('builder.resizePanels')}
        aria-orientation="vertical"
        aria-valuemin={MIN_EDITOR_WIDTH_PERCENT}
        aria-valuemax={MAX_EDITOR_WIDTH_PERCENT}
        aria-valuenow={resizeHandleValue}
        aria-valuetext={t('builder.editorWidthValue', { value: resizeHandleValue })}
        tabIndex={0}
        data-resizing={isResizing ? 'true' : undefined}
        onPointerDown={handleResizePointerDown}
        onPointerMove={handleResizePointerMove}
        onPointerUp={handleResizePointerUp}
        onPointerCancel={handleResizePointerUp}
        onKeyDown={handleResizeKeyDown}
      />

      <div className="cv-builder-preview">
        <div className="cv-builder-preview-header">
          <h2>{t('builder.preview')}</h2>
          <div className="cv-builder-preview-actions">
            <button
              type="button"
              className="cv-builder-settings-btn"
              onClick={() => setShowSettings(!showSettings)}
              aria-label={showSettings ? t('builder.hideSettings') : t('builder.showSettings')}
              aria-expanded={showSettings}
              aria-controls={settingsId}
            >
              <Settings aria-hidden="true" size={18} strokeWidth={2} />
            </button>
            {canExportPdf && (
              <button
                type="button"
                className="cv-builder-export-btn"
                onClick={handleExportPDF}
                disabled={isGenerating}
                aria-busy={isGenerating}
                aria-describedby={error ? exportErrorId : undefined}
              >
                <Download aria-hidden="true" size={16} strokeWidth={2} />
                <span>{isGenerating ? t('actions.generating') : t('actions.exportPdf')}</span>
              </button>
            )}
          </div>
        </div>

        {showSettings && (
          <fieldset
            id={settingsId}
            className="cv-builder-settings"
            aria-labelledby={settingsTitleId}
          >
            <legend id={settingsTitleId} className="cv-builder-settings-title">
              <SlidersHorizontal aria-hidden="true" size={16} strokeWidth={2} />
              <span>{t('builder.pdfSettings')}</span>
            </legend>
            <div className="cv-builder-settings-grid">
              <div className="cv-builder-margin-controls" aria-label={t('builder.pdfMargins')}>
                <MarginField
                  label={t('fields.top')}
                  value={margins.top}
                  onChange={(value) => updateMargin('top', value)}
                  decreaseLabel={t('actions.decreaseValue', { item: t('fields.top') })}
                  increaseLabel={t('actions.increaseValue', { item: t('fields.top') })}
                />
                <MarginField
                  label={t('fields.right')}
                  value={margins.right}
                  onChange={(value) => updateMargin('right', value)}
                  decreaseLabel={t('actions.decreaseValue', { item: t('fields.right') })}
                  increaseLabel={t('actions.increaseValue', { item: t('fields.right') })}
                />
                <MarginField
                  label={t('fields.bottom')}
                  value={margins.bottom}
                  onChange={(value) => updateMargin('bottom', value)}
                  decreaseLabel={t('actions.decreaseValue', { item: t('fields.bottom') })}
                  increaseLabel={t('actions.increaseValue', { item: t('fields.bottom') })}
                />
                <MarginField
                  label={t('fields.left')}
                  value={margins.left}
                  onChange={(value) => updateMargin('left', value)}
                  decreaseLabel={t('actions.decreaseValue', { item: t('fields.left') })}
                  increaseLabel={t('actions.increaseValue', { item: t('fields.left') })}
                />
              </div>
              <label className="cv-builder-setting-field cv-builder-locale-field">
                <span>{t('builder.cvLanguage')}</span>
                <select
                  value={cv.locale || DEFAULT_CV_LOCALE}
                  onChange={(event) => onChange({ locale: event.target.value })}
                >
                  {supportedLocales.map((locale) => (
                    <option key={locale} value={locale}>
                      {t(`locales.${locale}`)}
                    </option>
                  ))}
                </select>
              </label>
              <div className="cv-builder-guide-toggles" aria-label={t('builder.previewGuides')}>
                <button
                  type="button"
                  aria-pressed={showMarginGuides}
                  onClick={() => setShowMarginGuides((isVisible) => !isVisible)}
                >
                  {t('builder.showMarginGuides')}
                </button>
                <button
                  type="button"
                  aria-pressed={showContentGuide}
                  onClick={() => setShowContentGuide((isVisible) => !isVisible)}
                >
                  {t('builder.showContentGuide')}
                </button>
              </div>
            </div>
          </fieldset>
        )}

        {error && (
          <p id={exportErrorId} className="cv-builder-error" role="alert">
            {error.message}
          </p>
        )}
        <PaginatedPreview
          cv={cv}
          scale={previewScale}
          margins={margins}
          showMarginGuides={showMarginGuides}
          showContentGuide={showContentGuide}
        />
      </div>

      <button
        type="button"
        className="cv-builder-mobile-pane-toggle"
        onClick={() =>
          setMobilePane((currentPane) => (currentPane === 'editor' ? 'preview' : 'editor'))
        }
      >
        {mobilePane === 'editor' ? (
          <Eye aria-hidden="true" size={18} strokeWidth={2} />
        ) : (
          <Pencil aria-hidden="true" size={18} strokeWidth={2} />
        )}
        <span>{mobilePaneToggleLabel}</span>
      </button>
    </div>
  )
}

interface MarginFieldProps {
  label: string
  value: number
  onChange: (value: number) => void
  decreaseLabel: string
  increaseLabel: string
}

function MarginField({ label, value, onChange, decreaseLabel, increaseLabel }: MarginFieldProps) {
  const inputId = useId()

  return (
    <div className="cv-builder-setting-field">
      <label htmlFor={inputId}>{label}</label>
      <span className="cv-builder-margin-stepper">
        <button
          type="button"
          className="cv-builder-margin-stepper-btn"
          onClick={() => onChange(value - MARGIN_STEP_MM)}
          disabled={value <= MIN_MARGIN_MM}
          aria-label={decreaseLabel}
        >
          <Minus aria-hidden="true" size={14} strokeWidth={2.5} />
        </button>
        <input
          id={inputId}
          type="number"
          min={MIN_MARGIN_MM}
          max={MAX_MARGIN_MM}
          step={MARGIN_STEP_MM}
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        <span className="cv-builder-margin-unit">mm</span>
        <button
          type="button"
          className="cv-builder-margin-stepper-btn"
          onClick={() => onChange(value + MARGIN_STEP_MM)}
          disabled={value >= MAX_MARGIN_MM}
          aria-label={increaseLabel}
        >
          <Plus aria-hidden="true" size={14} strokeWidth={2.5} />
        </button>
      </span>
    </div>
  )
}

// ─── Locale flag switcher ────────────────────────────────────────────────────

function LocaleSwitcher({
  locales,
  current,
  onChange,
  getLabel,
  ariaLabel,
}: {
  locales: string[]
  current: string
  onChange: (locale: string) => void
  getLabel: (locale: string) => string
  ariaLabel: string
}) {
  return (
    <fieldset className="cv-builder-locale-switcher">
      <legend className="cv-builder-sr-only">{ariaLabel}</legend>
      {locales.map((locale) => (
        <button
          key={locale}
          type="button"
          className="cv-builder-locale-btn"
          aria-pressed={current === locale}
          aria-label={getLabel(locale)}
          title={getLabel(locale)}
          onClick={() => onChange(locale)}
        >
          <LocaleFlag locale={locale} />
        </button>
      ))}
    </fieldset>
  )
}

function LocaleFlag({ locale }: { locale: string }) {
  if (locale === 'da-DK' || locale.startsWith('da')) return <FlagDK />
  if (locale === 'en-US' || locale.startsWith('en')) return <FlagGB />
  return (
    <span className="cv-builder-locale-code" aria-hidden="true">
      {locale.slice(0, 2).toUpperCase()}
    </span>
  )
}

function FlagDK() {
  return (
    <svg
      viewBox="0 0 28 20"
      aria-hidden="true"
      className="cv-locale-flag"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="28" height="20" fill="#C60C30" />
      <rect x="9" width="4" height="20" fill="white" />
      <rect y="8" width="28" height="4" fill="white" />
    </svg>
  )
}

function FlagGB() {
  return (
    <svg
      viewBox="0 0 60 30"
      aria-hidden="true"
      className="cv-locale-flag"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="60" height="30" fill="#012169" />
      {/* St Andrew's white saltire */}
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="white" strokeWidth="10" fill="none" />
      {/* St Patrick's red saltire (simplified — no counterchange at small size) */}
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="3.33" fill="none" />
      {/* St George's white fimbriation */}
      <rect x="24" width="12" height="30" fill="white" />
      <rect y="12" width="60" height="6" fill="white" />
      {/* St George's red cross */}
      <rect x="26" width="8" height="30" fill="#C8102E" />
      <rect y="13" width="60" height="4" fill="#C8102E" />
    </svg>
  )
}

// ─── Header icon ──────────────────────────────────────────────────────────────

function VHLogoMark() {
  return (
    <svg
      className="cv-builder-vh-logo"
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="2" y="2" width="44" height="44" rx="12" fill="currentColor" opacity="0.2" />
      <text
        x="24"
        y="30"
        fill="currentColor"
        fontFamily="Arial, Helvetica, sans-serif"
        fontSize="17"
        fontWeight="800"
        letterSpacing="0"
        textAnchor="middle"
      >
        VH
      </text>
    </svg>
  )
}
